import { useCallback, useEffect, useRef } from "react";
import logger from "logging/logger";
import { RouteProps, useHistory } from "react-router";
import { useSelector } from "react-redux";
import { selectUserId } from "modules/auth/redux/selectors";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { EventRole } from "modules/event/constants";
import { useAppDispatch } from "store/hooks";
import {
  prefillPreferredLoginMethods,
  setInviteeDetails,
  showUserPreferredLoginBypass,
} from "modules/auth/redux/authSlice";
import { IUser } from "modules/app/types";
import { getMethodsFromUserData } from "modules/auth/utils/getMethodsFromUserData";
import { setShowRegisterDialog } from "modules/eventLanding/redux/eventLandingSlice";
import useAuth from "modules/auth/hooks/useAuth";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import useEventInviteActions from "./hooks/useEventInviteActions";

interface IEventInviteAuthenticatorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match?: any;
}

export const INVITE_CHECK_TIMEOUT = 5000;

const EventInviteAuthenticator = (
  props: IEventInviteAuthenticatorProps & RouteProps,
) => {
  const { match } = props;
  const { signOut } = useAuth();
  const userId = useSelector(selectUserId);
  const { checkIfEventEnded } = useCheckEventStatus();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { checkInviteCode } = useEventInviteActions();
  const { addErrorNotification } = useNotificationActions();
  const { inviteCode } = match.params;
  const { t } = useI18n(["common", "server"]);

  const check = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (!inviteCode) {
      addErrorNotification({ message: t("missing.invite.token") });
      history.push("/home");

      return;
    }
    let resp;

    if (userId) {
      resp = await checkInviteCode(inviteCode, userId);
    } else {
      resp = await checkInviteCode(inviteCode);
    }

    if (resp.isSuccess) {
      if (resp.isInvitee) {
        history.push(`/e/${resp?.event?.code}/register`);

        return;
      }

      if (userId && resp.event && !checkIfEventEnded(resp.event)) {
        await signOut();
        dispatch(setShowRegisterDialog(true));
      }

      if (
        resp.email &&
        (resp.role === EventRole.speaker ||
          (resp.event && resp.event.isPrivate))
      ) {
        dispatch(
          setInviteeDetails({
            inviteeEmail: resp.email,
            inviteeRole: resp.role as EventRole,
          }),
        );

        if (resp.inviteeLoginMethods) {
          const methods = getMethodsFromUserData(
            resp.inviteeLoginMethods as Partial<IUser>,
          );

          if (methods.length > 1 || resp.inviteeLoginMethods.saml) {
            if (userId && resp.event && !checkIfEventEnded(resp.event)) {
              dispatch(showUserPreferredLoginBypass(methods));
            } else {
              dispatch(prefillPreferredLoginMethods(methods));
            }
          }
        }
      }

      history.push(`/e/${resp?.event?.code}/register`);
    } else {
      logger.warn(
        "[EventInviteAuthenticator] error encountered when validating event code",
        { resp, inviteCode, userId },
      );

      addErrorNotification({ message: resp.message && t(resp.message) });
      history.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode, userId]);

  useEffect(() => {
    if (userId) {
      check();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      check();
    }, INVITE_CHECK_TIMEOUT);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <PageLoader message={t("wait") as string} />;
};

export default EventInviteAuthenticator;
